import {
  collection, doc, getDocs, getDoc, addDoc, updateDoc, deleteDoc,
  serverTimestamp, query, where, orderBy, writeBatch,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { ReviewCycle, TeamScorecard } from '../types';

const COL_CYCLE = 'reviewCycles';
const COL_TEAM  = 'teamScorecards';
const COL_FORMS = 'reviewForms';
const COL_USERS = 'users';

// ─── Review Cycles ────────────────────────────────────────────────────────────

export async function fetchCycles(): Promise<ReviewCycle[]> {
  const snap = await getDocs(query(collection(db, COL_CYCLE), orderBy('createdAt', 'desc')));
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as ReviewCycle);
}

export async function fetchCycle(id: string): Promise<ReviewCycle | null> {
  const snap = await getDoc(doc(db, COL_CYCLE, id));
  return snap.exists() ? ({ id: snap.id, ...snap.data() } as ReviewCycle) : null;
}

export async function fetchActiveCycle(): Promise<ReviewCycle | null> {
  const snap = await getDocs(query(collection(db, COL_CYCLE), where('status', '==', 'active')));
  if (snap.empty) return null;
  return { id: snap.docs[0].id, ...snap.docs[0].data() } as ReviewCycle;
}

export type CycleInput = Omit<ReviewCycle, 'id' | 'createdAt' | 'updatedAt'>;

export async function createCycle(data: CycleInput): Promise<string> {
  const ref = await addDoc(collection(db, COL_CYCLE), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function updateCycle(id: string, data: Partial<CycleInput>): Promise<void> {
  await updateDoc(doc(db, COL_CYCLE, id), { ...data, updatedAt: serverTimestamp() });
}

export async function deleteCycle(id: string): Promise<void> {
  await deleteDoc(doc(db, COL_CYCLE, id));
}

/**
 * Activate a cycle: set status=active and bulk-create ReviewForm docs for all
 * active users who have a directManagerId set (or for admins/leaders themselves).
 */
export async function activateCycle(
  cycleId: string,
  activatedBy: string,
): Promise<{ formsCreated: number }> {
  // 1. Get all active users
  const usersSnap = await getDocs(
    query(collection(db, COL_USERS), where('isActive', '==', true)),
  );

  // 2. Find which reviewees already have a form for this cycle
  const existingSnap = await getDocs(
    query(collection(db, COL_FORMS), where('cycleId', '==', cycleId)),
  );
  const existingIds = new Set(existingSnap.docs.map(d => d.data().revieweeId as string));

  // 3. Bulk-create forms in batches of 400
  const eligible = usersSnap.docs.filter(d => {
    const u = d.data();
    return (u.directManagerId || u.isLeader) && !existingIds.has(d.id);
  });

  let created = 0;
  const BATCH_SIZE = 400;

  for (let i = 0; i < eligible.length; i += BATCH_SIZE) {
    const batch = writeBatch(db);
    const chunk = eligible.slice(i, i + BATCH_SIZE);

    for (const uDoc of chunk) {
      const u = uDoc.data();
      const ref = doc(collection(db, COL_FORMS));
      batch.set(ref, {
        cycleId,
        revieweeId: uDoc.id,
        revieweeName: u.name || u.email || uDoc.id,
        reviewerId: u.directManagerId || activatedBy,
        reviewerName: '',
        teamScorecardId: null,
        status: 'self_pending',
        selfReview: {},
        responses360Ids: [],
        managerReview: {},
        auditLog: [{ action: 'form_created', performedBy: activatedBy, timestamp: new Date().toISOString() }],
        createdAt: serverTimestamp(),
      });
      created++;
    }

    await batch.commit();
  }

  // 4. Mark cycle active
  await updateDoc(doc(db, COL_CYCLE, cycleId), {
    status: 'active',
    updatedAt: serverTimestamp(),
  });

  return { formsCreated: created };
}

export async function lockCycle(cycleId: string): Promise<void> {
  await updateDoc(doc(db, COL_CYCLE, cycleId), {
    status: 'locked',
    updatedAt: serverTimestamp(),
  });
}

export async function closeCycle(cycleId: string): Promise<void> {
  await updateDoc(doc(db, COL_CYCLE, cycleId), {
    status: 'closed',
    updatedAt: serverTimestamp(),
  });
}

// ─── Team Scorecards ──────────────────────────────────────────────────────────

export async function fetchTeamScorecards(cycleId: string): Promise<TeamScorecard[]> {
  const snap = await getDocs(
    query(collection(db, COL_TEAM), where('cycleId', '==', cycleId)),
  );
  return snap.docs.map(d => ({ id: d.id, ...d.data() }) as TeamScorecard);
}

export type ScorecardInput = Omit<TeamScorecard, 'id' | 'createdAt' | 'updatedAt'>;

export async function upsertTeamScorecard(data: ScorecardInput): Promise<string> {
  // Check if one exists for this cycle+unit combo
  const existing = await getDocs(
    query(
      collection(db, COL_TEAM),
      where('cycleId', '==', data.cycleId),
      where('unitId', '==', data.unitId),
    ),
  );
  if (!existing.empty) {
    const id = existing.docs[0].id;
    await updateDoc(doc(db, COL_TEAM, id), { ...data, updatedAt: serverTimestamp() });
    return id;
  }
  const ref = await addDoc(collection(db, COL_TEAM), { ...data, createdAt: serverTimestamp() });
  return ref.id;
}

export async function deleteTeamScorecard(id: string): Promise<void> {
  await deleteDoc(doc(db, COL_TEAM, id));
}
